import { Component, ElementRef, HostListener, Input, OnDestroy, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil, take } from 'rxjs';
import { SESSION_ID } from 'src/app/constants/authentication.constants';
import { splitFilename } from 'src/app/helpers/file-helpers';
import { AutosaveModel } from 'src/app/models/autosave.model';
import { AutosaveService } from 'src/app/services/autosave.service';
import { DialogService } from 'src/app/services/dialog.service';
import { WindowService } from 'src/app/services/window.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'tx-recover-documents-dialog',
  templateUrl: './recover-documents-dialog.component.html',
  styleUrls: ['./recover-documents-dialog.component.scss'],
})
export class RecoverDocumentsDialogComponent implements OnDestroy {
  @Input() staffID: string = '';
  @Input() casID: string = '';
  @Input() casDescription: string = '';
  @Input() clientName: string = '';
  @Input() staffId: string = '';
  @Input() autosaves: Array<AutosaveModel> = [];
  selectedAutosave: AutosaveModel | null;
  public submitUrl: string;
  public sessionId = SESSION_ID;
  private unsubscribe$ = new Subject<void>();
  @ViewChild('openInNewTabForm') openInNewTabForm!: ElementRef;

  constructor(
    public activeModal: NgbActiveModal,
    private dialogService: DialogService,
    private autosaveService: AutosaveService,
    private windowService: WindowService
  ) {
    this.submitUrl = environment.endpoints.apiUrl + '/autosave/recoverdocumentinnewsession';
    this.selectedAutosave = null;
  }

  @HostListener('document:visibilitychange', ['$event'])
  onDocumentVisibilityChange(event: Event): void {
    if (document.visibilityState === 'visible') {
      this.refreshAutosaveList();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onAutosaveSelected(a: AutosaveModel) {
    this.selectedAutosave = a;

    // set hidden form values for open in new tab functionality
    const sessionIdInput = document.getElementById('autosaveSessionId') as HTMLInputElement;
    if (sessionIdInput) {
      sessionIdInput.value = this.selectedAutosave?.sessionId ?? '';
    }

    const extensionInput = document.getElementById('extension') as HTMLInputElement;
    if (extensionInput) {
      extensionInput.value = splitFilename(a.filename).extension;
    }

    const htmlSessionId = document.getElementById(SESSION_ID) as HTMLInputElement;
    if (sessionIdInput) {
      htmlSessionId.value = this.windowService.getSessionIdFromWindow() ?? '';
    }
  }

  onOpenInNewTabClicked(a: AutosaveModel) {
    this.onAutosaveSelected(a);
    this.openInNewTabForm.nativeElement.submit();
    const idx = this.autosaves.findIndex((e) => e.sessionId === a.sessionId);
    this.autosaves.splice(idx, 1);
    if (this.autosaves.length === 0) {
      this.activeModal.dismiss();
    }
  }

  onDeleteClicked(a: AutosaveModel) {
    return this.dialogService.confirm({
      title: 'Delete recovered document',
      message: 'Do you want to delete this recovered document?',
      confirmButtonText: 'Delete',
      rejectButtonText: 'Cancel',
      size: 'sm',
      confirmCallback: () => {
        this.delete(a);
      },
      rejectCallback: () => {},
    });
  }

  refreshAutosaveList() {
    if (this.casID) {
      this.getAutosavesByCasId();
    } else if (this.staffID) {
      this.getAutosavesByStaffId();
    }
  }

  private getAutosavesByCasId() {
    this.autosaveService.getByCasId(this.casID)
    .pipe(take(1))
    .subscribe((res: AutosaveModel[]) => {
      if (res) {
        this.autosaves = res;
        if (this.autosaves.length === 0) {
          this.activeModal.dismiss();
        }
      }
    })
  }

  private getAutosavesByStaffId() {
    this.autosaveService.getByStaffId(this.staffID)
    .pipe(take(1))
    .subscribe((res: AutosaveModel[]) => {
      if (res) {
        this.autosaves = res;
        if (this.autosaves.length === 0) {
          this.activeModal.dismiss();
        }
      }
    })
  }

  private delete(a: AutosaveModel) {
    this.autosaveService
      .delete(a.sessionId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        const idx = this.autosaves.findIndex((e) => e.sessionId === a.sessionId);
        this.autosaves.splice(idx, 1);
        if (this.autosaves.length === 0) {
          this.activeModal.dismiss();
        }
      });
  }
}
